import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CustomDomainSystemSettings, UpdateCustomDomainSystemSettingsRequest } from '@claude-code-api/shared';
import { Gauge, Globe2, Settings2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, Input, Label, Skeleton } from '@/components/ui';
import { Separator } from '@/components/ui/separator';

interface SettingsApiResponse {
  data?: CustomDomainSystemSettings;
  error?: string;
}

const FIELD_BOUNDS: Record<keyof UpdateCustomDomainSystemSettingsRequest, readonly [number, number]> = {
  maxDomainsPerUser: [1, 1000],
  dnsFailureThreshold: [1, 100],
  reconcileIntervalSeconds: [5, 3600],
  dnsRevalidationIntervalSeconds: [60, 86_400],
  certificateCheckIntervalSeconds: [300, 86_400],
  certificateExpiryWarningDays: [1, 365],
};

const LIMIT_FIELDS = ['maxDomainsPerUser', 'dnsFailureThreshold'] as const;
const CONTROLLER_FIELDS = ['reconcileIntervalSeconds', 'certificateCheckIntervalSeconds'] as const;
const HEALTH_FIELDS = ['dnsRevalidationIntervalSeconds', 'certificateExpiryWarningDays'] as const;
const UNIT_SUFFIX_FIELDS: ReadonlySet<keyof UpdateCustomDomainSystemSettingsRequest> = new Set([
  'reconcileIntervalSeconds',
  'certificateCheckIntervalSeconds',
  'dnsRevalidationIntervalSeconds',
  'certificateExpiryWarningDays',
]);
const AUTO_SAVE_DELAY_MS = 600;

function settingsAreValid(settings: UpdateCustomDomainSystemSettingsRequest): boolean {
  return Object.entries(settings).every(([key, value]) => {
    const [minimum, maximum] = FIELD_BOUNDS[key as keyof UpdateCustomDomainSystemSettingsRequest];
    return Number.isSafeInteger(value) && value >= minimum && value <= maximum;
  });
}

export default function AdminCustomDomainSettingsPage() {
  const { t } = useTranslation(['admin', 'common']);
  const [settings, setSettings] = useState<UpdateCustomDomainSystemSettingsRequest | null>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSettingsRef = useRef<UpdateCustomDomainSystemSettingsRequest | null>(null);
  const lastPersistedSettingsRef = useRef<string | null>(null);
  const savingRef = useRef(false);
  const mountedRef = useRef(true);

  const flushPendingSettings = useCallback(async (): Promise<void> => {
    if (savingRef.current || !pendingSettingsRef.current) return;

    const nextSettings = pendingSettingsRef.current;
    pendingSettingsRef.current = null;
    savingRef.current = true;

    try {
      const response = await fetch('/api/admin/custom-domain/settings', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(nextSettings),
      });
      const result = await response.json() as SettingsApiResponse;
      if (!response.ok || !result.data) throw new Error(result.error ?? t('admin:customDomainSettings.errors.save'));

      const { updatedAt: _updatedAt, ...persistedSettings } = result.data;
      lastPersistedSettingsRef.current = JSON.stringify(persistedSettings);
    } catch (error) {
      if (mountedRef.current) {
        toast.error(error instanceof Error ? error.message : t('admin:customDomainSettings.errors.save'));
      }
    } finally {
      savingRef.current = false;
      if (pendingSettingsRef.current) void flushPendingSettings();
    }
  }, [t]);

  useEffect(() => {
    let cancelled = false;
    void fetch('/api/admin/custom-domain/settings', { credentials: 'include' })
      .then(async (response) => {
        const result = await response.json() as SettingsApiResponse;
        if (!response.ok || !result.data) throw new Error(result.error ?? t('admin:customDomainSettings.errors.load'));
        if (!cancelled) {
          const { updatedAt: _updatedAt, ...values } = result.data;
          lastPersistedSettingsRef.current = JSON.stringify(values);
          setSettings(values);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) toast.error(error instanceof Error ? error.message : t('admin:customDomainSettings.errors.load'));
      });
    return () => { cancelled = true; };
  }, [t]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    if (!settings || !settingsAreValid(settings)) return;

    const serializedSettings = JSON.stringify(settings);
    if (serializedSettings === lastPersistedSettingsRef.current) return;

    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null;
      pendingSettingsRef.current = settings;
      void flushPendingSettings();
    }, AUTO_SAVE_DELAY_MS);

    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
    };
  }, [flushPendingSettings, settings]);

  const updateField = (key: keyof UpdateCustomDomainSystemSettingsRequest, value: string): void => {
    const parsed = Number(value);
    setSettings((current) => current ? { ...current, [key]: parsed } : current);
  };

  if (!settings) return <div className="space-y-4"><Skeleton className="h-16" /><Skeleton className="h-80" /></div>;

  const renderField = (field: keyof UpdateCustomDomainSystemSettingsRequest) => {
    const hasUnitSuffix = UNIT_SUFFIX_FIELDS.has(field);
    return (
      <div key={field} className="space-y-2">
        <Label htmlFor={`custom-domain-setting-${field}`}>
          {t(`admin:customDomainSettings.fields.${field}.label`)}
        </Label>
        {hasUnitSuffix ? (
          <div className="flex w-full max-w-sm overflow-hidden rounded-md border border-input bg-background shadow-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
            <Input
              id={`custom-domain-setting-${field}`}
              className="rounded-none border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
              type="number"
              inputMode="numeric"
              min={FIELD_BOUNDS[field][0]}
              max={FIELD_BOUNDS[field][1]}
              step={1}
              value={settings[field]}
              onChange={(event) => updateField(field, event.target.value)}
              aria-describedby={`custom-domain-setting-${field}-help`}
            />
            <span className="flex min-w-20 items-center justify-center border-l bg-muted px-3 text-sm text-muted-foreground">
              {t(`admin:customDomainSettings.fields.${field}.unit`)}
            </span>
          </div>
        ) : (
          <Input
            id={`custom-domain-setting-${field}`}
            className="w-full max-w-sm shadow-none"
            type="number"
            inputMode="numeric"
            min={FIELD_BOUNDS[field][0]}
            max={FIELD_BOUNDS[field][1]}
            step={1}
            value={settings[field]}
            onChange={(event) => updateField(field, event.target.value)}
            aria-describedby={`custom-domain-setting-${field}-help`}
          />
        )}
        <p id={`custom-domain-setting-${field}-help`} className="text-xs text-muted-foreground">
          {t(`admin:customDomainSettings.fields.${field}.help`)}
        </p>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader title={t('admin:customDomainSettings.title')} description={t('admin:customDomainSettings.description')} />
      <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1.35fr)_minmax(320px,0.85fr)]">
        <Card className="overflow-hidden shadow-none">
          <CardHeader className="flex-row items-center space-y-0 border-b bg-muted/30 px-5 py-4">
            <CardTitle className="flex items-center gap-2 text-base">
              <Settings2 className="h-4 w-4 text-primary" />
              {t('admin:customDomainSettings.panels.global')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6 p-5">
            <section className="space-y-4">
              <div className="space-y-1">
                <h2 className="flex items-center gap-2 text-sm font-semibold">
                  <Globe2 className="h-4 w-4 text-muted-foreground" />
                  {t('admin:customDomainSettings.groups.limits.title')}
                </h2>
                <CardDescription>{t('admin:customDomainSettings.groups.limits.description')}</CardDescription>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">{LIMIT_FIELDS.map(renderField)}</div>
            </section>

            <Separator />

            <section className="space-y-4">
              <div className="space-y-1">
                <h2 className="flex items-center gap-2 text-sm font-semibold">
                  <Gauge className="h-4 w-4 text-muted-foreground" />
                  {t('admin:customDomainSettings.groups.controller.title')}
                </h2>
                <CardDescription>{t('admin:customDomainSettings.groups.controller.description')}</CardDescription>
              </div>
              <div className="space-y-4">{CONTROLLER_FIELDS.map(renderField)}</div>
            </section>
          </CardContent>
        </Card>

        <div>
          <Card className="overflow-hidden shadow-none">
            <CardHeader className="flex-row items-center space-y-0 border-b bg-muted/30 px-5 py-4">
              <CardTitle className="flex items-center gap-2 text-base">
                <ShieldCheck className="h-4 w-4 text-primary" />
                {t('admin:customDomainSettings.panels.health')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5 p-5">
              {renderField(HEALTH_FIELDS[0])}
              <Separator />
              {renderField(HEALTH_FIELDS[1])}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
